import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import ControllerInput from "../../../components/form/ControllerInput";
import ControllerSelect from "../../../components/form/ControllerSelect";
import FormSection from "../../../components/form/FormSection";
import AlertMessage from "../../../components/form/AlertMessage";
import {
  getClasses,
} from "../../../api/dropdownApi";
import {
  getTermsByClass,
  getDivisionsByClass,
  getSubjectsByClass,
  getChaptersByClassDivTermSub,
  getTopicsByChapters,
} from "../../../api/dependentDropdownApi";
import { addSyllabus } from "../../../api/Api"; 

const AddSyllabus = () => {
  const { control, watch, handleSubmit, setValue, reset } = useForm();

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);

  const selectedClass = watch("class_id");
  const selectedDiv = watch("division_id");
  const selectedTerm = watch("term_id");
  const selectedSubject = watch("subject_id");
  const selectedChapter = watch("chapter_id");

  /** Fetch all classes */
  const fetchClasses = async () => {
    try {
      const response = await getClasses();
      const resData = response.data;
      if (resData.success) {
        setClasses(resData.data);
      } else {
        setClasses([]);
      }
    } catch (err) {
      console.error(err);
      setClasses([]);
    }
  };
 
  useEffect(() => {
    if (selectedClass) {
      Promise.all([
        getTermsByClass(selectedClass),
        getDivisionsByClass(selectedClass),
        getSubjectsByClass(selectedClass),
      ])
        .then(([termRes, divRes, subjRes]) => {
          setTerms(termRes.data.data || []);
          setDivisions(divRes.data.data || []);
          setSubjects(subjRes.data.data || []);
        })
        .catch(() => {
          setTerms([]);
          setDivisions([]);
          setSubjects([]);
        });
 
      setValue("term_id", "");
      setValue("division_id", "");
      setValue("subject_id", "");
      setValue("chapter_id", "");
      setValue("topic_id", "");
    } else {
      setTerms([]);
      setDivisions([]);
      setSubjects([]);
      setChapters([]);
      setTopics([]);
      setValue("term_id", "");
      setValue("division_id", "");
      setValue("subject_id", "");
      setValue("chapter_id", "");
      setValue("topic_id", "");
    }
  }, [selectedClass, setValue]);

  useEffect(() => {
    if (selectedSubject) {
      getChaptersByClassDivTermSub(
        selectedClass,
        selectedDiv,
        selectedTerm,
        selectedSubject
      )
        .then((chapterRes) => {
          setChapters(chapterRes.data.data || []);
        })
        .catch(() => {
          setChapters([]);
        });
    } else {
      setChapters([]);
      setTopics([]);
      setValue("chapter_id", "");
      setValue("topic_id", "");
    }
  }, [selectedClass, selectedDiv, selectedTerm, selectedSubject, setValue]);

  useEffect(() => {
    if (selectedChapter) {
      getTopicsByChapters(selectedChapter)
        .then((topicRes) => {
          setTopics(topicRes.data.data || []);
        })
        .catch(() => {
          setTopics([]);
        });
    } else {
      setTopics([]);
      setValue("topic_id", "");
    }
  }, [selectedChapter, setValue]);

  useEffect(() => {
    fetchClasses();
  }, []);

  const onSubmit = async (data) => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      const response = await addSyllabus(data);
      if (response.data.success) {
        setSuccessMessage("Syllabus added successfully!");
        reset();
      } else {
        setErrorMessage(response.data.message || "Failed to save syllabus.");
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Something went wrong while saving syllabus.");
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between mb-3">
        <FormSection title={"Add Syllabus"} />
      </div>

      <AlertMessage
        type="danger"
        message={errorMessage}
        onClose={() => setErrorMessage("")}
      />
      <AlertMessage
        type="success"
        message={successMessage}
        onClose={() => setSuccessMessage("")}
      />

      <div className="card">
        <div className="card-body">
          <form className="form-group" onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="class_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Class is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Class"
                      options={classes.map((c) => ({
                        label: c.class_name || c.name,
                        value: c.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="term_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Term is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Term"
                      options={terms.map((t) => ({
                        label: t.term || t.name,
                        value: t.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>
 
            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="division_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Division is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Division"
                      options={divisions.map((d) => ({
                        label: d.division || d.name,
                        value: d.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="subject_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Subject is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Subject"
                      options={subjects.map((s) => ({
                        label: s.subject_name || s.name,
                        value: s.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="chapter_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Chapter is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Chapter"
                      options={chapters.map((s) => ({
                        label: s.chapter_name || s.name,
                        value: s.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="topic_id"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Topic is required" }}
                  render={({ field, fieldState }) => (
                    <ControllerSelect
                      label="Topic"
                      options={topics.map((s) => ({
                        label: s.topic_title || s.name,
                        value: s.id,
                      }))}
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>
 
            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="lecture_date"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Lecture date required" }}
                  render={({ field, fieldState }) => (
                    <ControllerInput
                      label="Lecture Date"
                      type="date"
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Controller
                  name="number_of_lecture"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Total lectures required",
                    min: { value: 1, message: "Minimum 1 lecture" },
                  }}
                  render={({ field, fieldState }) => (
                    <ControllerInput
                      label="Total Lectures Taken"
                      type="number"
                      {...field}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="mt-3">
              <button className="btn btn-success" type="submit">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSyllabus;